import { api } from '../utils/api';
import { ChallengeServiceData } from '../types/challenge.types';

export interface Notification {
  id: string;
  type: 'challenge_request' | 'challenge_accepted' | 'challenge_declined' | 'challenge_edited' | 'challenge_started' | 'general';
  title: string;
  message: string;
  status: 'unread' | 'read';
  data?: any;
  createdAt: string;
  senderId?: string;
  senderName?: string;
}



class NotificationService {
  private static instance: NotificationService;

  private constructor() {}

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  async getNotifications(): Promise<{ success: boolean; data: Notification[] }> {
    try {
      const response = await api.get('/notifications');
      if (response.data.success && response.data.data) {
        // Validate and filter notifications to ensure they have required fields
        const validNotifications = response.data.data.filter((notification: any) => {
          if (!notification.id) {
            console.warn('Notification missing ID:', notification);
            return false;
          }
          if (!notification.type || !notification.title || !notification.message) {
            console.warn('Notification missing required fields:', notification);
            return false;
          }
          return true;
        });
        return { success: true, data: validNotifications };
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  async getUnreadCount(): Promise<{ success: boolean; count: number }> {
    try {
      const response = await api.get('/notifications/unread-count');
      return response.data;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  async markAsRead(notificationId: string): Promise<{ success: boolean }> {
    try {
      const response = await api.patch(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async markAllAsRead(): Promise<{ success: boolean }> {
    try {
      const response = await api.patch('/notifications/mark-all-read');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  async deleteNotification(notificationId: string): Promise<{ success: boolean }> {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  async clearAllNotifications(): Promise<{ success: boolean }> {
    try {
      const response = await api.delete('/notifications');
      return response.data;
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  }

  // Challenge request specific methods
  async sendChallengeRequest(data: ChallengeServiceData): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.post('/competitors/challenge-request', data);
      return response.data;
    } catch (error) {
      console.error('Error sending challenge request:', error);
      throw error;
    }
  }

  async acceptChallengeRequest(challengeId: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.post('/competitors/challenge-request/accept', { challengeId });
      return response.data;
    } catch (error) {
      console.error('Error accepting challenge request:', error);
      throw error;
    }
  }

  async declineChallengeRequest(challengeId: string, reason: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.post('/competitors/challenge-request/decline', { challengeId, reason });
      return response.data;
    } catch (error) {
      console.error('Error declining challenge request:', error);
      throw error;
    }
  }

  async editChallengeRequest(challengeId: string, data: ChallengeServiceData): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.post('/competitors/challenge-request/edit', { challengeId, ...data });
      return response.data;
    } catch (error) {
      console.error('Error editing challenge request:', error);
      throw error;
    }
  }

  // Send notification to challenge sender when their challenge is accepted
  async notifyChallengeAccepted(challengeId: string, senderId: string, senderName: string, opponentName: string): Promise<{ success: boolean }> {
    try {
      const notificationData = {
        type: 'challenge_accepted',
        title: 'Challenge Accepted! 🎉',
        message: `${opponentName} has accepted your challenge request!`,
        recipientId: senderId,
        data: {
          challengeId,
          opponentName,
          type: 'challenge_accepted'
        }
      };

      const response = await api.post('/notifications', notificationData);
      return response.data;
    } catch (error) {
      console.error('Error sending challenge acceptance notification:', error);
      throw error;
    }
  }

  // Send notification to challenge sender when their challenge is declined
  async notifyChallengeDeclined(challengeId: string, senderId: string, senderName: string, opponentName: string, reason: string): Promise<{ success: boolean }> {
    try {
      const notificationData = {
        type: 'challenge_declined',
        title: 'Challenge Declined',
        message: `${opponentName} has declined your challenge request. Reason: ${reason}`,
        recipientId: senderId,
        data: {
          challengeId,
          opponentName,
          reason,
          type: 'challenge_declined'
        }
      };

      const response = await api.post('/notifications', notificationData);
      return response.data;
    } catch (error) {
      console.error('Error sending challenge decline notification:', error);
      throw error;
    }
  }

  // Send notification to challenge sender when their challenge is edited
  async notifyChallengeEdited(challengeId: string, senderId: string, senderName: string, opponentName: string, originalData: any, editedData: any): Promise<{ success: boolean }> {
    try {
      const notificationData = {
        type: 'challenge_edited',
        title: 'Challenge Request Edited',
        message: `${opponentName} has edited your challenge request and sent it back to you.`,
        recipientId: senderId,
        data: {
          challengeId,
          opponentName,
          type: 'challenge_edited',
          // Original data (what was sent initially)
          originalArea: originalData.area,
          originalMode: originalData.mode,
          originalStake: originalData.stake,
          originalNumberOfQuestions: originalData.numberOfQuestions,
          originalStartTime: originalData.startTime,
          originalDuration: originalData.duration,
          originalMessage: originalData.message,
          // Edited data (what was changed)
          area: editedData.area,
          mode: editedData.mode,
          stake: editedData.stake,
          numberOfQuestions: editedData.numberOfQuestions,
          startTime: editedData.startTime,
          duration: editedData.duration,
          message: editedData.message,
          editedAt: new Date().toISOString()
        }
      };

      const response = await api.post('/notifications', notificationData);
      return response.data;
    } catch (error) {
      console.error('Error sending challenge edit notification:', error);
      throw error;
    }
  }

  // Accept edited challenge request
  async acceptEditedChallengeRequest(challengeId: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.post('/competitors/challenge-request/accept-edited', { challengeId });
      return response.data;
    } catch (error) {
      console.error('Error accepting edited challenge request:', error);
      throw error;
    }
  }

  // Reject edited challenge request
  async rejectEditedChallengeRequest(challengeId: string, reason: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.post('/competitors/challenge-request/reject-edited', { challengeId, reason });
      return response.data;
    } catch (error) {
      console.error('Error rejecting edited challenge request:', error);
      throw error;
    }
  }

  // Get challenge history for tracking edits
  async getChallengeHistory(challengeId: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.get(`/competitors/challenge-request/${challengeId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching challenge history:', error);
      throw error;
    }
  }

  // Emergency notification methods for compatibility
  async notifyEmergencyContacts(emergency: any): Promise<void> {
    try {
      // This is a placeholder for emergency notifications
      // The actual implementation would depend on your emergency system
      console.log('Emergency notification sent:', emergency);
    } catch (error) {
      console.error('Error sending emergency notification:', error);
      throw error;
    }
  }

  async notifyEmergencyResolution(emergency: any): Promise<void> {
    try {
      // This is a placeholder for emergency resolution notifications
      console.log('Emergency resolution notification sent:', emergency);
    } catch (error) {
      console.error('Error sending emergency resolution notification:', error);
      throw error;
    }
  }

  async sendEmergencyNotifications(contacts: any[]): Promise<void> {
    try {
      // This is a placeholder for sending emergency notifications to contacts
      console.log('Emergency notifications sent to contacts:', contacts);
    } catch (error) {
      console.error('Error sending emergency notifications to contacts:', error);
      throw error;
    }
  }
}

export default NotificationService.getInstance(); 